
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import FileRepository from '@/repositories/upload/FileRepository';
import FileUpload from '@/repositories/data/upload/FileUpload';
import { BehaviorSubject, from, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import Resource from '@/repositories/Resource';

@Component
export default class FileUploader extends Vue {
    @Prop({ required: false, type: String, default: '' })
    public readonly id!: string;

    @Prop({ required: false, type: String, default: '' })
    public readonly label!: string;

    @Prop({ required: false, type: Boolean, default: false })
    public readonly video!: boolean;

    public file: File | null = null;
    public fileUpload: FileUpload | null = null;
    public loading = true;
    public error: string | null = null;

    @Inject()
    private readonly fileRepository!: FileRepository;

    @Prop({ required: false, type: Boolean, default: false })
    private readonly required!: boolean;

    @Prop({ required: false, type: Number, default: null })
    private readonly value!: number | null;

    private fileLoadSubscription = new Subscription();
    private fileUploadSubscription = new Subscription();
    private valueSubject = new BehaviorSubject<number | null>(null);

    public created(): void {
        this.valueSubject.next(this.value);
        this.fileLoadSubscription = this.valueSubject
            .pipe(
                switchMap((id) =>
                    id !== null ? this.fileRepository.getFile(id) : from([Resource.success<FileUpload>(null)]),
                ),
            )
            .subscribe((fileUploadResource) => {
                if (fileUploadResource.isSuccess) {
                    this.fileUpload = fileUploadResource.data;
                } else if (fileUploadResource.isError) {
                    this.error = 'general.communicationError';
                }
                this.loading = fileUploadResource.isLoading;
            });
    }

    public beforeDestroy(): void {
        this.fileLoadSubscription.unsubscribe();
        this.fileUploadSubscription.unsubscribe();
    }

    @Watch('value')
    public onValueChanged(value: number | null): void {
        this.valueSubject.next(value);
    }

    @Watch('file')
    public onFileChanged(file: File | null): void {
        if (file === null && this.fileUpload === null) {
            this.$emit('input', null);
        }
        if (file !== null) {
            this.error = null;
            this.loading = true;
            this.fileUploadSubscription.unsubscribe();
            this.fileUploadSubscription = this.fileRepository
                .uploadFile(file, this.video)
                .subscribe((fileUploadResource) => {
                    if (fileUploadResource.isSuccess) {
                        const fileUpload = fileUploadResource.data;
                        this.$emit('input', fileUpload ? fileUpload.id : null);
                        this.fileUpload = fileUpload;
                    } else if (fileUploadResource.isError) {
                        this.$emit('error', fileUploadResource.error);
                        this.error = 'general.fileUploadError';
                    }
                    this.loading = fileUploadResource.isLoading;
                });
        }
    }

    public onDelete(): void {
        this.error = null;
        this.loading = true;
        this.file = null;
        this.fileUpload = null;
        this.$emit('input', null);
        this.loading = false;
    }

    public get rules(): ((value: any) => boolean | string)[] {
        return this.required ? [(value) => Boolean(value)] : [];
    }

    public get isImage(): boolean {
        let imageExtensions = ['gif', 'jpg', 'jpeg', 'png', 'apng', 'svg', 'bmp'];
        if (this.fileUpload != null) {
            let extension = this.fileUpload.name.split('.')[this.fileUpload.name.split('.').length - 1];
            return imageExtensions.includes(extension);
        } else {
            return false;
        }
    }
}
